import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, MailCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../utils/api';
import { OffertifyLogo } from '../components/OffertifyLogo';

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email obbligatoria').email('Email non valida'),
  tenant_slug: z.string().optional(),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword: React.FC = () => {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '', tenant_slug: '' },
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      await authAPI.forgotPassword({
        email: data.email,
        tenant_slug: data.tenant_slug?.trim() || undefined,
      });
      // Always show generic success to avoid leaking which emails are registered
      setSubmittedEmail(data.email);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Errore durante la richiesta di reset. Riprova.'
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 flex items-center justify-center rounded-2xl bg-primary-50 shadow-sm border border-primary-100">
            <OffertifyLogo className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-secondary-900">
            Recupera password
          </h2>
          <p className="mt-2 text-sm text-secondary-600">
            Ti invieremo un link per reimpostare la password
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg border border-secondary-200 p-8">
          {submittedEmail ? (
            <div className="text-center space-y-4">
              <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-green-50">
                <MailCheck className="h-7 w-7 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">Controlla la tua email</h3>
                <p className="mt-2 text-sm text-secondary-600">
                  Se esiste un account associato a{' '}
                  <span className="font-medium text-secondary-900">{submittedEmail}</span>, riceverai
                  a breve un&apos;email con le istruzioni per reimpostare la password.
                </p>
                <p className="mt-2 text-xs text-secondary-500">
                  Non trovi l&apos;email? Controlla la cartella spam.
                </p>
              </div>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div>
                <label htmlFor="tenant_slug" className="block text-sm font-medium text-secondary-700 mb-1">
                  Tenant <span className="text-secondary-400 font-normal">(Opzionale)</span>
                </label>
                <input
                  id="tenant_slug"
                  type="text"
                  autoComplete="off"
                  className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 bg-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all sm:text-sm"
                  placeholder="Es. nome-azienda (lascia vuoto per Host)"
                  {...register('tenant_slug')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 bg-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all sm:text-sm"
                  placeholder="mario.rossi@azienda.it"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Invio in corso...
                    </div>
                  ) : (
                    'Invia link di reset'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-8 text-center text-sm text-secondary-600">
          <Link to="/login" className="inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Torna al login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
